<script lang="ts" setup>
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { useUserStore } from '@/store/user'
import { useNotificationsStore } from '@/store/notifications'
import { usePayrollChange } from '@/app/core/composable/routes/useChangePayroll'
import { notificationError } from '@/app/core/util/notificationUtils'
import { useI18n } from 'vue-i18n'
import Popper from 'vue3-popper'
import {
  DBtn,
  DTextField,
  DTable,
  DModal,
  DLoading,
  SearchMdIcon,
  Edit03Icon,
  LockUnlocked03Icon,
  Lock03Icon
} from 'front-library'
import {
  getSavedFormulas,
  SavedFormula,
  updateStateSavedFormula
} from '@/app/core/services/saved-formulas'
usePayrollChange(() => window.location.reload())

/* store, router, notifications */
const router = useRouter()
const store = useUserStore()
const notifications = useNotificationsStore()
const { t } = useI18n()

/* Datos componente Modal y Loading */
const showModal = ref(false)
const titleModal = ref('')
const descriptionModal = ref('')
const acceptQuestion = ref('')
const typeAlertModal = ref('')

const loading = ref(false)
const messageLoading = ref('')

/* Datos */
const search = ref()
const tableSavedFormula = ref(null)
const savedFormulaUpdateState = ref()

/* Datos componente table */
const columns = ref([
  { name: 'code', title: t('savedFormulas.list.code'), table: 'code' },
  { name: 'name', title: t('savedFormulas.list.name'), table: 'name' },
  {
    name: 'state',
    title: t('savedFormulas.list.status'),
    table: 'is_inactive'
  },
  {
    name: 'updated_at',
    title: t('savedFormulas.list.lastUpdate'),
    table: 'updated_at'
  },
  { name: 'actions', title: '' }
])
const rows = ref<SavedFormula[]>([])
const perPage = ref(0)
const offset = ref(0)
const totalRows = ref(0)
const sortBy = ref('')

/* Redirect's */
const createSavedFormula = () => {
  void router.push({ name: 'CreateSavedFormula' })
}
const updateSavedFormula = (id: any) => {
  void router.push({ name: 'UpdateSavedFormula', params: { id } })
}
const getSavedFormula = (id: any) => {
  void router.push({ name: 'GetSavedFormula', params: { id } })
}

/* Cambiar estado de la formula guardada */
const changeStateSavedFormula = (values: any) => {
  typeAlertModal.value = 'question'
  const text = values.is_inactive
    ? t('table.action.activate')
    : t('table.action.deactivate')
  savedFormulaUpdateState.value = [
    { name: values.name, id: values.id, is_inactive: values.is_inactive }
  ]
  showModal.value = true
  titleModal.value = t('savedFormulas.list.actionStatus', {
    status: text,
    savedFormula: values.name as string
  })
  descriptionModal.value = ''
  acceptQuestion.value = t('modal.acceptQuestion', { action: text })
}
const changeStateConfirm = async () => {
  showModal.value = false
  await updateStateSavedFormula(
    savedFormulaUpdateState.value[0].id,
    !savedFormulaUpdateState.value[0].is_inactive
  )
    .then(response => {
      notifications.addNotification({
        title: !savedFormulaUpdateState.value[0].is_inactive
          ? t('notification.disabled')
          : t('notification.activated'),
        text: t('savedFormulas.notification.changeStatus', {
          status: !savedFormulaUpdateState.value[0].is_inactive
            ? t('table.action.deactivated')
            : t('table.action.asset')
        }),
        type: 'success',
        time: 10000
      })
      void savedFormulas()
    })
    .catch((error: any) => {
      notificationError(error, 'savedFormulas', t, notifications, loading)
    })
}

/* Cerrar el componente Modal */
const closeModal = () => {
  showModal.value = false
}

/* Listar Formulas guardadas */
const savedFormulas = async () => {
  loading.value = true
  messageLoading.value = t('loading.data')
  try {
    const data: any = await getSavedFormulas(
      offset.value,
      perPage.value,
      sortBy.value,
      search.value === '' ? undefined : search.value
    )
    rows.value = data.results
    totalRows.value = data.totalItems
    loading.value = false
  } catch (error: any) {
    notificationError(error, 'savedFormulas', t, notifications, loading)
  }
}
</script>
<template>
  <DLoading v-show="loading" :message="messageLoading" />
  <DModal
    :open="showModal"
    :typeAlert="typeAlertModal"
    :title="titleModal"
    :description="descriptionModal"
    :acceptQuestion="acceptQuestion"
    @closeModal="closeModal"
    @otherMethod="changeStateConfirm()" />
  <div class="h-full px-5 overflow-auto scroll">
    <div class="bg-gray-50 pb-4">
      <div class="leading-4 pt-responsive">
        <p
          class="text-3xl font-semibold text-shadow"
          v-html="t('savedFormulas.list.title', 1)" />
        <p
          class="text-gray-500 text-shadow"
          v-html="t('savedFormulas.list.description')" />
      </div>
      <div class="sm:flex justify-between pt-8">
        <div class="flex mb-3 sm:mb-0 max-w-2xl sm:w-2/3 md:w-1/2 lg:w-5/12">
          <DTextField
            :placeholder="t('form.button.search') + '...'"
            :icon="'SearchLgIcon'"
            class="mr-2 w-full"
            v-model="search"
            @keyup.enter="savedFormulas" />
          <DBtn
            @click.prevent="savedFormulas"
            v-html="t('form.button.search')" />
        </div>
        <DBtn
          :icon="'PlusIcon'"
          type="button"
          @click="createSavedFormula()"
          v-if="store.hasPermission('saved_formula:create')">
          {{
            t('form.button.create', { form: t('savedFormulas.list.title', 0) })
          }}
        </DBtn>
      </div>
    </div>
    <DTable
      ref="tableSavedFormula"
      :columns="columns"
      :rows="rows"
      :total-rows="totalRows"
      :select-options="false"
      :text-no-data="t('table.noData')"
      :text-per-page="t('table.rowsPerPage')"
      @perPage=";(perPage = $event), savedFormulas()"
      @offset="offset = $event"
      @sort=";(sortBy = $event), savedFormulas()">
      <template #state="data: any">
        <div
          class="flex items-center rounded-full font-semibold w-fit text-sm py-1 px-3"
          :class="
            data.is_inactive
              ? 'bg-red-100 text-red-600'
              : 'bg-green-100 text-green-600'
          ">
          <div
            class="mr-2 w-1.5 h-1.5 rounded-full"
            :class="data.is_inactive ? 'bg-red-600' : 'bg-green-600'"></div>
          {{
            data.is_inactive
              ? t('table.action.inactive')
              : t('table.action.active')
          }}
        </div>
      </template>
      <template #actions="data: any">
        <div class="flex py-2.5 justify-end gap-1">
          <Popper
            v-if="store.hasPermission('saved_formula:get')"
            arrow
            offsetDistance="12"
            :content="t('table.action.detail')"
            :hover="true"
            placement="top"
            class="tooltip">
            <div
              class="bg-gray-50 rounded-md py-2"
              @click="getSavedFormula(data.id)">
              <SearchMdIcon class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
          <Popper
            v-if="
              store.hasPermission('saved_formula:update') && !data.is_inactive
            "
            arrow
            offsetDistance="12"
            :content="t('table.action.edit')"
            :hover="true"
            placement="top"
            class="tooltip">
            <div
              class="bg-gray-50 rounded-md py-2"
              @click="updateSavedFormula(data.id)">
              <Edit03Icon class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
          <Popper
            v-if="store.hasPermission('saved_formula:enable')"
            arrow
            offsetDistance="12"
            :content="
              data.is_inactive
                ? t('savedFormulas.list.activate')
                : t('savedFormulas.list.deactivate')
            "
            :hover="true"
            placement="top"
            class="tooltip">
            <div
              class="bg-gray-50 rounded-md py-2"
              @click="changeStateSavedFormula(data)">
              <Lock03Icon
                v-if="data.is_inactive"
                class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
              <LockUnlocked03Icon
                v-else
                class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
        </div>
      </template>
    </DTable>
  </div>
</template>
